import { useRef, useState } from "react";
import ccxt from "@cede/ccxt";
import {
  AddNewCexButton,
  AmountFieldWithTokens,
  Bubble,
  Button,
  DropListWithIconModal,
  EmptyState,
  EmptyStateTypeEnum,
  Tabs,
  theme,
} from "@cede/ds";
import { Any, OrderType } from "@cede/types";
import { formatBalance, getCurrencySymbol } from "@cede/utils";
import { getTradePathLabel } from "../../Send/hooks/utils";
import { PresentationalTradeProps } from "../types";
import { LimitPrice } from "./LimitPrice";
import { TradeRouteInfoModal } from "./TradeRouteInfoModal";
import { MainFooter, Page } from "@cedelabs/react-utils";
import { HelpOutline } from "@mui/icons-material";
import { Box, Checkbox, Skeleton, Tab, Tooltip, Typography, alpha } from "@mui/material";

const ASSETS_TO_SUBLIST_TITLES = {
  popular: "Trending tokens",
  rest: "All tokens",
};

export const PresentationalTradePage = ({ controller }: PresentationalTradeProps) => {
  const {
    tradeAbleAsset,
    availableAccounts,
    isSubmitButtonActive,
    portfolioAssets,
    assetBalance,
    switchToken,
    amountToIsLoading,
    submitTrade,
    tokenFromPrice,
    tokenToPrice,
    tradeForm,
    dispatchForm,
    addMax,
    minAmountFrom,
    warningMessage,
    currency,
    isSwitchable,
    disabledSwitchMessage,
    isSwitchIconHovered,
    setIsSwitchIconHovered,
    assetFromLoading,
    assetToLoading,
    setFocusedInput,
    fee,
    formErrors,
    cexFeeIsLoading,
    isLoadingBalances,
    addCexRedirect,
    shouldDisableCex,
    disabledCexText,
    allowUserInputAmountTo,
    tradePath,
    marketRate,
    canByPassApprovalTradeHub,
    setByPassApprovalTradeHub,
    isWidget,
    tabs,
    orderType,
    selectOrderType,
    quoteToken,
    price,
    isLimitOrderSupported,
  } = controller;

  const [tradeRouteInfoModalIsOpen, setTradeRouteInfoModalIsOpen] = useState(false);

  const FormErrors = () => {
    const errorKeys = Object.keys(formErrors);

    if (!errorKeys.length) {
      return null;
    }

    return (
      <Box
        display={"flex"}
        flexDirection={"column"}
        paddingY={theme.spacing()}
        mt={theme.spacing()}
        sx={{
          background: alpha(theme.palette.error.main, 0.3),
          border: `2px solid ${theme.palette.error.main}`,
          borderRadius: theme.custom.defaultBoxRadius,
          padding: theme.spacing(),
        }}
      >
        {errorKeys.map((key) => {
          return (
            <Typography key={key} color={theme.palette.error.main} textAlign="left">
              {formErrors[key]}
            </Typography>
          );
        })}
      </Box>
    );
  };

  const CexFees = () => {
    if (!tradeForm.tokenTo || !tradeForm.tokenFrom || (!cexFeeIsLoading && !fee)) return null;

    return (
      <Box display={"flex"} justifyContent={"space-between"}>
        <Typography sx={{ color: theme.palette.text.secondary, textAlign: "left" }}>Estimated CEX Fees:</Typography>
        {cexFeeIsLoading ? (
          <Skeleton variant="text" sx={{ width: "50px", height: "20px" }} />
        ) : (
          <Typography>
            {formatBalance(fee?.amount || 0)} {fee?.tokenSymbol || ""} (
            {formatBalance(fee?.refAmount ?? 0, {
              roundForFiat: true,
            })}{" "}
            {getCurrencySymbol("en-US", currency.symbol)})
          </Typography>
        )}
      </Box>
    );
  };

  const MinValue = () => {
    if (!tradeForm.tokenTo || !tradeForm.tokenFrom) return null;
    return (
      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" mt="2px">
        {tradeForm.tokenFrom && tradeForm.tokenTo && (
          <>
            <Typography color={theme.palette.text.secondary} textAlign="right">
              Minimum {tradeForm.tokenFrom.label} amount:
            </Typography>
            {minAmountFrom ? (
              <Typography data-testid="min-value">
                {minAmountFrom} {tradeForm.tokenFrom.label}
              </Typography>
            ) : (
              <Skeleton variant="text" sx={{ width: "50px", height: "20px" }} />
            )}
          </>
        )}
      </Box>
    );
  };

  const TradeRoute = () => {
    if (!tradePath || !tradeForm.noCommonMarket) return null;
    return (
      <Box display={"flex"} justifyContent={"space-between"}>
        <Typography color={theme.palette.text.secondary} textAlign="left">
          Trade route:
        </Typography>
        <Typography
          sx={{
            cursor: "pointer",
          }}
          fontWeight={"bold"}
          color={theme.palette.primary.main}
          onClick={() => setTradeRouteInfoModalIsOpen(true)}
        >
          View
        </Typography>
      </Box>
    );
  };

  const Price = () => {
    return (
      <Box display={"flex"} justifyContent={"space-between"}>
        <Typography color={theme.palette.text.secondary} textAlign="left">
          Price:
        </Typography>
        {!marketRate ? (
          <Skeleton variant="text" sx={{ width: "50px", height: "20px" }} />
        ) : (
          <Typography>
            1 {tradeForm.tokenFrom?.value} ={" "}
            {formatBalance(
              !tradeForm.market || tradeForm.tokenTo?.value === tradeForm.market.quote
                ? marketRate.bid
                : ccxt.Precise.stringDiv("1", marketRate.ask),
            )}{" "}
            {tradeForm.tokenTo?.value}
          </Typography>
        )}
      </Box>
    );
  };

  const shouldDisplayDescription = !!(tradeForm.tokenFrom && tradeForm.tokenTo) || !!tradePath;

  const anchor = useRef<HTMLDivElement>(null);

  return (
    <Page displayFlex>
      <Box display={"flex"} flexDirection={"column"} flex={1}>
        <TradeRouteInfoModal
          tradePath={getTradePathLabel(tradePath || [])}
          cexName={tradeForm.selectedAccount?.cexValue || ""}
          tokenFrom={tradeForm.tokenFrom?.value || ""}
          tokenTo={tradeForm.tokenTo?.value || ""}
          open={tradeRouteInfoModalIsOpen}
          setOpen={setTradeRouteInfoModalIsOpen}
        />
        {isLimitOrderSupported && (
          <Tabs
            sx={{
              paddingBottom: theme.spacing(1),
            }}
            value={orderType}
            onChange={(_, value) => selectOrderType(value)}
          >
            {tabs.map((tab) => (
              <Tab
                data-testid={`${tab.value}-tab`}
                label={tab.label}
                value={tab.value}
                sx={{
                  minHeight: "auto",
                  padding: "8px 16px",
                }}
              />
            ))}
          </Tabs>
        )}

        <Box>
          <Box
            display={"flex"}
            paddingX={0}
            paddingBottom={0}
            alignItems={"stretch"}
            justifyContent={"center"}
            width="100%"
            textAlign="left"
          >
            <Box
              sx={{
                "transition": ".2s linear",
                "transitionDelay": "0.1s",
                "width": "100%",
                "paddingBottom": 0,
                "&.half-width": {
                  width: "50%",
                },
              }}
              className={""}
            >
              <DropListWithIconModal
                inputProps={{
                  id: "select-account",
                  placeholder: "Account",
                }}
                title={"Select account"}
                value={tradeForm.selectedAccount}
                items={availableAccounts}
                onChange={(value: Any) => dispatchForm({ field: "selectedAccount", value })}
                actionButton={addCexRedirect ? <AddNewCexButton onClick={addCexRedirect} /> : undefined}
                shouldDisableItem={shouldDisableCex}
                disableModalPortal={true}
                disabledTextItem={disabledCexText}
              />
            </Box>
          </Box>
          {!tradeForm.selectedAccount && (
            <Typography
              component="p"
              fontSize="12px"
              color={theme.palette.text.secondary}
              textAlign="left"
              margin="4px 8px"
            >
              Please, choose an account to continue.
            </Typography>
          )}

          {warningMessage && (
            <Box marginTop={theme.custom.gutterMargin}>
              <EmptyState type={EmptyStateTypeEnum.WARNING} title={warningMessage} />
            </Box>
          )}
        </Box>

        {tradeForm.selectedAccount && !warningMessage && (
          <>
            <Box display="flex" flexDirection="column" flex={1}>
              <Box data-testid="trade-form">
                {orderType === "limit" && (
                  <LimitPrice
                    priceSymbol={quoteToken?.label || ""}
                    price={price}
                    limitPrice={tradeForm.limitPrice}
                    setLimitPrice={(price) => dispatchForm({ field: "limitPrice", value: price })}
                  />
                )}
                <Box
                  display="flex"
                  flexDirection="column"
                  mt={theme.spacing(2)}
                  alignItems="flex-end"
                  gap={"12px"}
                  position={"relative"}
                >
                  <AmountFieldWithTokens
                    assets={portfolioAssets || []}
                    amount={tradeForm.amountFrom || ""}
                    setAmount={(value) => dispatchForm({ field: "amountFrom", value })}
                    selectedAsset={tradeForm.tokenFrom}
                    setSelectedAsset={(value) => dispatchForm({ field: "tokenFrom", value })}
                    estimatedValue={tokenFromPrice * parseFloat((tradeForm.amountFrom || "0").replace(/\s/g, ""))}
                    currency={currency}
                    error={!!formErrors["amountFrom"]}
                    label="From"
                    loading={assetFromLoading || isLoadingBalances}
                    id="trade-amount-from"
                    onFocus={() => setFocusedInput("amountFrom")}
                    onBlur={() => setFocusedInput(null)}
                    availableBalance={
                      <Typography
                        variant="body2"
                        data-testid="max-amount"
                        onClick={addMax}
                        sx={{
                          "paddingTop": theme.spacing(1),
                          "fontSize": "12px",
                          "textAlign": "right",
                          ":hover": {
                            textDecoration: "underline",
                            cursor: "pointer",
                          },
                        }}
                      >
                        Available:{" "}
                        {tradeForm.tokenFrom?.value && assetBalance !== null
                          ? formatBalance(Number(assetBalance as string)) + " " + tradeForm.tokenFrom?.value
                          : "0"}
                      </Typography>
                    }
                  />

                  <Box
                    position={"absolute"}
                    sx={{
                      right: "50%",
                      top: "50%",
                      transform: "translateX(50%) translateY(-50%)",
                      zIndex: theme.custom.zIndex.extraLayer,
                    }}
                  >
                    <Bubble
                      anchor={anchor.current}
                      isOpen={isSwitchIconHovered && !isSwitchable}
                      hideArrow
                      background={theme.palette.background.paper}
                      sx={{
                        color: theme.palette.common.white,
                      }}
                    >
                      <Typography>{disabledSwitchMessage}</Typography>
                    </Bubble>
                    <Box
                      sx={{
                        "background": theme.palette.background.default,
                        "display": "inline-flex",
                        "alignItems": "center",
                        "justifyContent": "center",
                        "borderRadius": "100%",
                        "border": `2px solid ${theme.palette.divider}`,
                        "padding": "6px",
                        "transition": "0.5s",
                        ":hover": {
                          background: "#2E2E2E",
                          cursor: isSwitchable ? "pointer" : "not-allowed",
                        },
                      }}
                      onClick={switchToken}
                    >
                      <Box
                        component="svg"
                        width={"16px"}
                        height={"14px"}
                        viewBox="0 0 14 12"
                        ref={anchor}
                        onMouseEnter={() => setIsSwitchIconHovered(true)}
                        onMouseLeave={() => setIsSwitchIconHovered(false)}
                        data-testid={"switch-token"}
                        sx={{
                          "fill": "rgba(255, 255, 255, 0.56)",
                          ":hover": {
                            fill: isSwitchable ? theme.palette.primary.main : "rgba(255, 255, 255, 0.56)",
                          },
                        }}
                      >
                        <path
                          d="M13.1016 8.5021C13.1679 8.4403 13.2211 8.36578 13.258 8.28298C13.2949 8.20018 13.3147 8.1108 13.3163 8.02017C13.3179 7.92953 13.3012 7.83951 13.2673 7.75546C13.2333 7.67141 13.1828 7.59506 13.1187 7.53096C13.0546 7.46687 12.9783 7.41634 12.8942 7.38239C12.8102 7.34844 12.7201 7.33177 12.6295 7.33336C12.5389 7.33496 12.4495 7.3548 12.3667 7.39169C12.2839 7.42859 12.2094 7.48178 12.1476 7.5481L10.5996 9.0961L10.5996 1.2751C10.5996 1.09608 10.5285 0.924387 10.4019 0.7978C10.2753 0.671213 10.1036 0.600098 9.92457 0.600098C9.74555 0.600098 9.57386 0.671213 9.44727 0.7978C9.32068 0.924387 9.24957 1.09608 9.24957 1.2751L9.24957 9.0961L7.70157 7.5481C7.63977 7.48178 7.56525 7.42859 7.48245 7.39169C7.39965 7.3548 7.31027 7.33496 7.21964 7.33336C7.12901 7.33176 7.03898 7.34844 6.95493 7.38239C6.87088 7.41634 6.79453 7.46687 6.73043 7.53096C6.66634 7.59506 6.61581 7.67141 6.58186 7.75546C6.54791 7.83951 6.53124 7.92953 6.53284 8.02017C6.53444 8.1108 6.55427 8.20018 6.59117 8.28298C6.62806 8.36578 6.68125 8.4403 6.74757 8.5021L9.44757 11.2021C9.57413 11.3285 9.74569 11.3995 9.92457 11.3995C10.1034 11.3995 10.275 11.3285 10.4016 11.2021L13.1016 8.5021V8.5021ZM6.29757 4.4521C6.35936 4.51841 6.43389 4.57161 6.51668 4.6085C6.59948 4.64539 6.68887 4.66523 6.7795 4.66683C6.87013 4.66843 6.96016 4.65176 7.04421 4.61781C7.12826 4.58386 7.20461 4.53333 7.2687 4.46923C7.3328 4.40513 7.38333 4.32878 7.41728 4.24474C7.45123 4.16069 7.4679 4.07066 7.4663 3.98003C7.4647 3.88939 7.44486 3.80001 7.40797 3.71721C7.37108 3.63441 7.31789 3.55989 7.25157 3.4981L4.55157 0.798097C4.42501 0.671692 4.25344 0.600691 4.07457 0.600691C3.89569 0.600691 3.72413 0.671692 3.59757 0.798097L0.897568 3.4981C0.778336 3.62605 0.713427 3.7953 0.716512 3.97017C0.719597 4.14504 0.790438 4.31188 0.91411 4.43556C1.03778 4.55923 1.20463 4.63007 1.3795 4.63315C1.55437 4.63624 1.72361 4.57133 1.85157 4.4521L3.39957 2.9041L3.39957 10.7251C3.39957 10.9041 3.47068 11.0758 3.59727 11.2024C3.72386 11.329 3.89555 11.4001 4.07457 11.4001C4.25359 11.4001 4.42528 11.329 4.55187 11.2024C4.67845 11.0758 4.74957 10.9041 4.74957 10.7251L4.74957 2.9041L6.29757 4.4521Z"
                          fill="white"
                        />
                      </Box>
                    </Box>
                  </Box>
                  <AmountFieldWithTokens
                    assets={tradeAbleAsset}
                    amount={tradeForm.amountTo || ""}
                    setAmount={(value) => allowUserInputAmountTo && dispatchForm({ field: "amountTo", value })}
                    selectedAsset={tradeForm.tokenTo}
                    setSelectedAsset={(value) => dispatchForm({ field: "tokenTo", value })}
                    estimatedValue={tokenToPrice * parseFloat((tradeForm.amountTo || "0").replace(/\s/g, ""))}
                    currency={currency}
                    label="To"
                    loading={assetToLoading}
                    id="trade-amount-to"
                    onFocus={() => setFocusedInput("amountTo")}
                    onBlur={() => setFocusedInput(null)}
                    subListTitles={ASSETS_TO_SUBLIST_TITLES}
                    amountIsLoading={amountToIsLoading}
                  />
                </Box>
              </Box>
              {shouldDisplayDescription && (
                <Box
                  sx={{
                    width: "100%",
                    mt: "16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: theme.custom.smallGutterMargin,
                    background: theme.palette.background.darkPaper,
                    borderRadius: theme.custom.defaultBoxRadius,
                    paddingX: theme.spacing(),
                    paddingY: theme.spacing(1.5),
                  }}
                >
                  <MinValue />
                  <CexFees />
                  {orderType === OrderType.MARKET && <Price />}

                  <TradeRoute />
                </Box>
              )}
              {isWidget && !Object.keys(formErrors).length && (
                <Box
                  display={"flex"}
                  alignItems="center"
                  onClick={() => setByPassApprovalTradeHub(!canByPassApprovalTradeHub)}
                >
                  <Checkbox checked={canByPassApprovalTradeHub} />
                  <Box display="flex" alignItems="center" gap={theme.spacing()}>
                    <Typography variant="body1">Enable fast trade</Typography>
                    <Tooltip
                      title={
                        <Typography
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          fontSize="14px"
                          sx={{
                            "minWidth": "164px",
                            "transition": ".2s ease-out",
                            ":hover": {
                              opacity: 0.6,
                            },
                          }}
                        >
                          If enabled, the approval page won't be shown before executing the trade.
                        </Typography>
                      }
                      sx={{
                        fontSize: "20px",
                        color: theme.palette.text.primary,
                      }}
                      placement="top"
                      arrow
                      PopperProps={{
                        disablePortal: true,
                      }}
                    >
                      <HelpOutline color="inherit" fontSize="inherit" />
                    </Tooltip>
                  </Box>
                </Box>
              )}

              <FormErrors />
            </Box>

            <MainFooter>
              <Button
                variant={"contained"}
                disabled={!isSubmitButtonActive}
                height="tall"
                fullWidth
                data-testid={"trade-button"}
                onClick={submitTrade}
              >
                Confirm
              </Button>
            </MainFooter>
          </>
        )}
      </Box>
    </Page>
  );
};
